import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, forkJoin, map, mergeMap, of, startWith, throwError } from 'rxjs';
import { FrontendConfigApi } from 'src/app/shared/api/frontend-config.api';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { OrderApi } from '../api/order.api';
import {
  AllStateCountyFips,
  GenerativeCommentConfig,
  GeocodeApiResponse,
  GeocodeSearchParams,
  OrderRoutePathConfig,
} from '../interface/order.interface';
import { OrderState } from '../state/order.state';

import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { JSONPath } from 'jsonpath-plus';
import { CookieService } from 'ngx-cookie-service';
import { MarkerType } from 'src/app/module/google-map/interface/google-map.interface';
import { AutomationApiResponse } from 'src/app/shared/interface/billing.interface';
import { AppPermission, OrderPermission } from 'src/app/shared/interface/common.interface';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { ProxyService } from 'src/app/shared/services/proxy.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AccessControlState } from 'src/app/shared/state/access-control.state';
import { CompanyState } from 'src/app/shared/state/company.state';
import { UserState } from 'src/app/shared/state/user.state';
import { environment as ENV } from 'src/environments/environment';
import { AsyncDataState } from '../../async/state/async-data.state';
import { LongTermRentalState } from '../../long-term-rental/state/long-term-rental.state';
import { OrderStatus } from '../../order-list/interface/order-list.interface';
import { PropertyAttribute, PropertyDetail, StandardStatusType } from '../../property/interface/property.interface';
import { PropertyState } from '../../property/state/property.state';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { ShortTermRentalState } from '../../short-term-rental/state/short-term-rental.state';
import { SyncScope, SyncUpdateSet } from '../../sync/interface/sync.interface';
import { SyncService } from '../../sync/service/sync.service';
import { ValidationState } from '../../validation/state/validation.state';

import { ConfigService } from 'src/app/shared/services/config.service';
import { OrderConfig } from '../config/order.config';
import { OrderFeedback } from '../interface/feedback.interface';
import { SimilarityScoreAttributes } from '../interface/similarity-score.interface';
import { CostApproachState } from '../state/cost-approach.state';
import { SalesApproachState } from '../state/sales-approach.state';
import { RouteTreeNode } from './route-tree.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(
    private _cookieService: CookieService,
    private _router: Router,
    private accessControlState: AccessControlState,
    private asyncDataState: AsyncDataState,
    private companyState: CompanyState,
    private costApproachState: CostApproachState,
    private frontendConfigApi: FrontendConfigApi,
    private longTermRentalState: LongTermRentalState,
    private orderApi: OrderApi,
    private orderState: OrderState,
    private progressLoaderService: ProgressLoaderService,
    private propertyState: PropertyState,
    private proxyService: ProxyService,
    private salesApproachState: SalesApproachState,
    private scenarioState: ScenarioState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private shortTermRentalState: ShortTermRentalState,
    private snackBarService: SnackBarService,
    private syncService: SyncService,
    private validationState: ValidationState,
    private userState: UserState,
    private configService: ConfigService
  ) {}

  getOrderDetail(orderId?: string | null, referenceId?: string | null, action?: string | null): Observable<boolean> {
    const loaderId = this.progressLoaderService.show('Getting Order Details');
    return this.orderApi.getOrderDetail(orderId, referenceId, action).pipe(
      mergeMap((order) => {
        this.syncService.syncLocally(SyncScope.ORDER, order);

        this.orderState.orderSetting = this.companyState.companySetting[order.reportType];

        this._cookieService.set('profet-ctx', JSON.stringify({ orderObjectId: order.id }), {
          secure: true,
          domain: ENV.appDomain,
          path: '/',
          sameSite: 'None',
        });

        if (this.orderState.orderSetting?.salesComparison) {
          this.salesApproachState.secondarySearchFilter =
            this.orderState.orderSetting.salesComparison.secondarySearchFilter;
        }

        if (this.orderState.orderSetting.costApproach) {
          this.costApproachState.secondarySearchFilter =
            this.orderState.orderSetting.costApproach?.secondarySearchFilter;
        }

        this.shortTermRentalState.secondarySearchFilter =
          this.orderState.orderSetting.shortTermRental?.secondarySearchFilter ?? null;
        this.longTermRentalState.secondarySearchFilter =
          this.orderState.orderSetting.longTermRental?.secondarySearchFilter ?? null;

        return forkJoin([
          this.configService.getFieldConfig(order.reportType),
          this.configService.getEnumeration(order.reportType),
        ]).pipe(map(() => true));
      }),
      catchError((error: Error) => {
        if (this.userState.permittedActions.includes(AppPermission.ORDER_LIST)) {
          this._router.navigate(['/orders']);
        } else {
          this._router.navigate(['/not-found']);
        }

        return throwError(() => error);
      }),
      finalize(() => this.progressLoaderService.hide(loaderId))
    );
  }

  computeSimilarityScore(similarityScores: SimilarityScoreAttributes): Observable<boolean> {
    return this.orderApi
      .computeSimilarityScore(this.orderState.orderDetail?.id, this.scenarioState.activeScenario!.id, similarityScores)
      .pipe(
        map(() => false),
        startWith(true),
        catchError(() => of(false))
      );
  }

  runAutomation(): Observable<AutomationApiResponse> {
    return this.orderApi.runAutomation(this.orderState.orderDetail.id, this.scenarioState.activeScenario!.id).pipe(
      map((res) => {
        if (res.orderStatus === OrderStatus.automated) {
          this._router.navigate(['/orders']);
        }
        return res;
      }),
      catchError(({ error }) => {
        this.snackBarService.open('error', error.message);
        return of({} as AutomationApiResponse);
      })
    );
  }

  getGeocode(reqParams: GeocodeSearchParams): Observable<GeocodeApiResponse> {
    return this.orderApi.getGeocode(reqParams);
  }

  getAccessControl(orderId: string): Observable<OrderPermission[]> {
    return this.orderApi.getAccessControl(orderId).pipe(
      map((res) => {
        if (!res) {
          this.accessControlState.activeOrderId = orderId;
          this.accessControlState.permittedActions = [];
          return [];
        }

        this.accessControlState.activeOrderId = orderId;
        this.accessControlState.permittedActions = res;
        this.orderState.readOnlyMode = this.orderState.lockReadOnly || !res.includes(OrderPermission.ORDER_EDIT);

        return res;
      }),
      catchError(() => {
        this.accessControlState.activeOrderId = orderId;
        this.accessControlState.permittedActions = [];
        return of([]);
      })
    );
  }

  getMarkerType(status?: StandardStatusType): MarkerType {
    let markerType: MarkerType;
    switch (status) {
      case StandardStatusType.sold:
        markerType = 'sold';
        break;

      case StandardStatusType.active:
      case StandardStatusType.activeUC:
        markerType = 'active';
        break;

      case StandardStatusType.pending:
        markerType = 'pending';
        break;

      case StandardStatusType.canceled:
      case StandardStatusType.delisted:
      case StandardStatusType.expired:
        markerType = 'disabled';
        break;

      default:
        markerType = 'sold';
        break;
    }
    return markerType;
  }

  getPrice(property: PropertyDetail, dataTransform: boolean = true) {
    const standardStatus = dataTransform
      ? this.selectedSourceDataPipe.transform(property.StandardStatus)
      : property.StandardStatus;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return standardStatus && standardStatus.toLowerCase() == 'sold'
      ? dataTransform
        ? this.selectedSourceDataPipe.transform(property.ClosePrice) ||
          this.selectedSourceDataPipe.transform(property.LastSalePrice)
        : (property.ClosePrice ?? property.LastSalePrice)
      : dataTransform
        ? this.selectedSourceDataPipe.transform(property.ListPrice)
        : property.ListPrice;
  }

  calculatePricePerSqftLand(property: PropertyDetail, dataTransform: boolean = true) {
    const price = this.getPrice(property, dataTransform);

    const lotSizeSquareFeet = dataTransform
      ? this.selectedSourceDataPipe.transform(property.LotSizeSquareFeet)
      : property.LotSizeSquareFeet;

    if (Number(price) && Number(lotSizeSquareFeet)) {
      return (price / lotSizeSquareFeet)?.toFixed(2)?.toString() || '';
    } else {
      return '';
    }
  }

  calculatePricePerSqft(property: PropertyDetail, dataTransform: boolean = true) {
    const price = this.getPrice(property, dataTransform);

    const livingArea = dataTransform ? this.selectedSourceDataPipe.transform(property.LivingArea) : property.LivingArea;

    if (Number(price) && Number(livingArea)) {
      return (price / livingArea)?.toFixed(2)?.toString() || '';
    } else {
      return '';
    }
  }

  updateOrder(updateSet: SyncUpdateSet[]) {
    return this.orderApi.updateOrder(this.orderState.orderDetail?.id, this.orderState.reportType, updateSet).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getInspectionRecord(reqParams: any) {
    return this.orderApi.getInspectionRecord(reqParams).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  updateInspectionDetails(reqParams: any): Observable<{ status: boolean; message: string }> {
    return this.orderApi.updateInspectionDetails(reqParams).pipe(
      map(() => {
        this.snackBarService.open('success', 'Inspection data updated successfully');
        return { status: true, message: '' };
      }),
      catchError(({ error }: HttpErrorResponse) => {
        // this.snackBarService.open('error', (error?.message as string) ?? 'Unable to fetch inspection data');
        return of({ status: false, message: (error?.message as string) ?? 'Unable to fetch inspection data' });
      })
    );
  }

  /** Builds the route tree from default config, applying company setting filters and optional runtime exclusions */
  initializeRouteConfig(excludedRoutes: string[] = []) {
    const availableList: OrderRoutePathConfig[] = this.filterConfig(
      OrderConfig.defaultOrderRoutePathList,
      excludedRoutes
    );
    OrderConfig.orderRoutePathList = [...availableList];
    OrderConfig.routeTreeConfig = this.createRouteTree(null, {
      route: '',
      displayValue: '',
      jsonPath: '',
      companySettingJsonPath: null,
      validationGroup: [],
      children: OrderConfig.orderRoutePathList,
      validationLabel: '',
      validationRoute: '',
    });
  }

  /**This function is used to create route tree class */
  createRouteTree(
    parent: RouteTreeNode<OrderRoutePathConfig> | null,
    node: OrderRoutePathConfig
  ): RouteTreeNode<OrderRoutePathConfig> {
    let routeNode: RouteTreeNode<OrderRoutePathConfig>;
    if (parent == null) {
      routeNode = new RouteTreeNode<OrderRoutePathConfig>(node);
    } else {
      routeNode = parent.addChild(node);
    }
    if (node.children) {
      for (const c of node.children) {
        this.createRouteTree(routeNode, c);
      }
    }
    return routeNode;
  }

  filterConfig(configList: OrderRoutePathConfig[], excludedRoutes: string[] = []): OrderRoutePathConfig[] {
    return configList
      .filter((config) => {
        if (excludedRoutes.includes(config.validationRoute)) return false;

        if (config.companySettingJsonPath) {
          const status = [];

          if (Array.isArray(config.companySettingJsonPath)) {
            config.companySettingJsonPath?.forEach((element) => {
              const modifiedPath = `${this.orderState.reportType}.${element}`;

              const jsonValueWITHModifiedPath: unknown = JSONPath({
                json: this.companyState.companySetting,
                path: modifiedPath,
                wrap: false,
              });
              const jsonValueWITHOUTModifiedPath: unknown = JSONPath({
                json: this.companyState.companySetting,
                path: element,
                wrap: false,
              });

              status.push(jsonValueWITHModifiedPath, jsonValueWITHOUTModifiedPath);
            });
          } else {
            const modifiedPath = `${this.orderState.reportType}.${config.companySettingJsonPath}`;

            const jsonValueWITHModifiedPath: unknown = JSONPath({
              json: this.companyState.companySetting,
              path: modifiedPath,
              wrap: false,
            });
            const jsonValueWITHOUTModifiedPath: unknown = JSONPath({
              json: this.companyState.companySetting,
              path: config.companySettingJsonPath,
              wrap: false,
            });

            status.push(jsonValueWITHModifiedPath, jsonValueWITHOUTModifiedPath);
          }

          //Checking path condition
          let conditionStatus = true;
          if (config.condition) {
            const conditionModifiedPath = `${this.orderState.reportType}.${config.condition?.route}`;

            const jsonValueConditionModifiedPath: unknown = JSONPath({
              json: this.companyState.companySetting,
              path: conditionModifiedPath,
              wrap: false,
            });

            if (jsonValueConditionModifiedPath != config.condition?.value) conditionStatus = false;
          }

          if (status.some((x) => x == true) && conditionStatus) return true;
          else return false;
        } else return true;
      })
      .map((config) => {
        const filteredChildren = config.children ? this.filterConfig(config.children, excludedRoutes) : [];

        return {
          ...config,
          children: filteredChildren,
        };
      });
  }

  getAllStateCountyFips() {
    return this.orderApi.getAllStateCountyFips().pipe(
      map((res: AllStateCountyFips) => res.counties),
      catchError(() => of())
    );
  }

  getMLSBoards(params: any): Observable<any> {
    return this.proxyService.getMLSBoards(params);
  }

  /**function is used to clear order state while navigating to other routes */
  clearState() {
    this._cookieService.delete('profet-ctx', '/', ENV.appDomain, true, 'None');
    this.syncService.syncLocally(SyncScope.ORDER, null);

    this.syncService.syncLocally(SyncScope.SALES_SELECTED_COMPS, {});
    this.salesApproachState.comparableList = [];
    this.salesApproachState.comparableListLength = 0;
    this.salesApproachState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.COST_SELECTED_COMPS, {});
    this.costApproachState.comparableList = [];
    this.costApproachState.comparableListLength = 0;
    this.costApproachState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS, {});
    this.shortTermRentalState.comparableList = [];
    this.shortTermRentalState.comparableListLength = 0;
    this.shortTermRentalState.secondarySearchFilter = null;

    this.syncService.syncLocally(SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS, {});
    this.longTermRentalState.comparableList = [];
    this.longTermRentalState.comparableListLength = 0;
    this.longTermRentalState.secondarySearchFilter = null;

    this.asyncDataState.asyncPendingData = [];

    this.validationState.selectedRule = null;
    OrderConfig.routeTreeConfig?.clearValidation();
    this.validationState.unmatchedExternalValidations = [];

    this.validationState.validationAuditTime = null;
    this.validationState.validationSheetToggle = false;

    this.syncService.syncLocally(SyncScope.SUBJECT, {});
    this.propertyState.activePropertyData = null;
    this.propertyState.subjectPageLayout = null;

    this.orderState.cachedPreviewHtml = undefined;
    this.orderState.cachedAdditionalDocs = [];
    this.orderState.previewSnapshots = undefined;

    this.syncService.syncLocally(SyncScope.SCENARIO, null);
    this.syncService.syncLocally(SyncScope.SCENARIO_MAP, null);

    this.orderState.propertyStore.clear();
    this.orderState.selectedComparableStoreByScenario.clear();
    this.orderState.selectedComparableStoreByValuationType.clear();
    this.orderState.selectedComparableStoreByScenarioValuationType.clear();

    this.accessControlState.clear();
    this.orderState.readOnlyMode = false;
    this.orderState.lockReadOnly = false;

    this.propertyState.subjectImageLayout = null;
    this.propertyState.comparableImageLayout = null;
    this.propertyState.comparablePageLayout = null;
    this.propertyState.propertyConditionLayout = null;

    //TODO: Need to check if Neighborhood State needs to be cleared.
  }

  /** This function is used to get all routes path through recursively
   */
  getAllRouteNames(routes: OrderRoutePathConfig[] = OrderConfig.orderRoutePathList, pathList: string[] = []) {
    routes.forEach((r) => {
      if (r.children.length) {
        this.getAllRouteNames(r.children, pathList);
      } else {
        pathList.push(r.validationRoute);
      }
    });

    return pathList;
  }

  /** Get street address  */
  getUnparsedAddress(data: Partial<Record<PropertyAttribute, any>> | null) {
    const streetNumber = data?.StreetNumber ? data?.StreetNumber + ' ' : '';
    const streetName = data?.StreetName ? data?.StreetName + ' ' : '';
    const unitNumber = data?.UnitNumber ? '#' + data?.UnitNumber : '';

    return (streetNumber + streetName + unitNumber).trim();
  }

  calculateDistance(subjectData: { lat: any; lng: any }, compData: { lat: any; lng: any }) {
    if (subjectData.lat && subjectData.lng && compData.lat && compData.lng) {
      const EARTH_RADIUS_MILES = 3958.75;

      const toRad = (deg: number) => (deg * Math.PI) / 180;

      const lat1Rad = toRad(Number(subjectData.lat));
      const lon1Rad = toRad(Number(subjectData.lng));
      const lat2Rad = toRad(Number(compData.lat));
      const lon2Rad = toRad(Number(compData.lng));

      const deltaLat = lat2Rad - lat1Rad;
      const deltaLon = lon2Rad - lon1Rad;

      const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return EARTH_RADIUS_MILES * c;
    } else return null;
  }

  calculateProximityDirection(subjectData: { lat: any; lng: any }, compData: { lat: any; lng: any }): string | null {
    if (subjectData.lat == null || subjectData.lng == null || compData.lat == null || compData.lng == null) {
      return null;
    }

    const lat1 = Number(subjectData.lat);
    const lon1 = Number(subjectData.lng);
    const lat2 = Number(compData.lat);
    const lon2 = Number(compData.lng);

    if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) {
      return null;
    }

    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const toDeg = (rad: number) => (rad * 180) / Math.PI;

    const lat1Rad = toRad(lat1);
    const lat2Rad = toRad(lat2);
    const deltaLonRad = toRad(lon2 - lon1);

    const y = Math.sin(deltaLonRad) * Math.cos(lat2Rad);

    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLonRad);

    let bearing = toDeg(Math.atan2(y, x));

    // Convert bearing to 0–360°
    bearing = (bearing + 360) % 360;

    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

    const index = Math.round(bearing / 45) % 8;

    return directions[index];
  }

  getOrderFeedback(orderId: string): Observable<OrderFeedback[]> {
    return this.orderApi.getOrderFeedback(orderId);
  }

  public generateComment(config: GenerativeCommentConfig): Observable<string> {
    const orderId = this.orderState.orderDetail.id;
    return this.orderApi.generateComment(orderId, config.commentType, config.scenarioId, config.propertyId);
  }
}
